import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  STORY_CAPTION_MAX,
  STORY_PHOTO_MAX_BYTES,
  STORY_VIDEO_MAX_BYTES,
  STORY_VIDEO_MAX_SECONDS,
} from '@goaltree/backend/convex/lib/stories'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { type StickerKind, isEdited } from '@goaltree/ui/lib/photo-effects'
import {
  POST_KINDS,
  POST_KIND_COLORS,
  POST_MAX,
  type PostKind,
  postPermalink,
  profileUrl,
} from '@goaltree/ui/lib/social-share'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import type { TFunction } from 'i18next'
import { Check, CircleCheck, Flag, Globe, ImagePlus, Target, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CanopyAvatar } from '@/components/canopy-avatar'
import {
  PhotoEditorCanvas,
  PhotoEditorControls,
  usePhotoEdit,
  useStoryStickers,
} from '@/components/photo-editor'
import { PostedToast } from '@/components/posted-toast'
import { StreakFlame } from '@/components/streak-icons'
import { UserAvatar } from '@/components/user-avatar'
import { useGoals } from '@/hooks/use-goals'
import { useIsMobile } from '@/hooks/use-mobile'
import { isMacPlatform } from '@/lib/capture'
import { bakePhoto } from '@/lib/photo-bake'
import { optimisticSetHandle } from '@/lib/prefs-optimistic'
import {
  STORY_PHOTO_MAX_PX,
  STORY_PHOTO_QUALITY,
  downscaleImage,
  readStoryClip,
} from '@/lib/story-media'

export type ComposerMode = 'post' | 'story'
type Starter = FunctionReturnType<typeof api.posts.postStarters>[number]
type Goal = NonNullable<ReturnType<typeof useGoals>>[number]
type Draft = {
  text: string
  kind: PostKind | null
  goalId: Id<'nodes'> | null
  starter: string | null
}
type SetDraft = (fn: (d: Draft) => Draft) => void
type Picked =
  | { kind: 'photo'; file: File; blob: Blob; preview: string }
  | { kind: 'video'; file: File; poster: Blob; frames: Blob[]; preview: string }
const EMPTY: Draft = { text: '', kind: null, goalId: null, starter: null }
const MB = 1024 * 1024

export const KIND_TEXT: Record<PostKind, string> = {
  win: 'text-[#3f8f1f] dark:text-[#7ed957]',
  progress: 'text-[#1d74c4] dark:text-[#5cb8ff]',
  struggle: 'text-[#b45309] dark:text-[#fbbf24]',
  question: 'text-[#0f766e] dark:text-[#2dd4bf]',
}

const starterKey = (s: Starter) => `${s.kind}:${s.goalId ?? ''}:${s.subject ?? s.count ?? ''}`
const STORY_ASPECT = 9 / 16

const toCaption = (text: string) => text.replace(/\s*\n\s*/g, ' ').slice(0, STORY_CAPTION_MAX)

// Sentinel spliced into the label so the task/milestone title can be emphasised
// wherever the locale's word order puts it.
const SUBJECT = '\u0001'

function starterCopy(t: TFunction, s: Starter) {
  const subject = s.subject ?? ''
  if (s.kind === 'streak')
    return {
      icon: <StreakFlame />,
      label: t('postComposer.starter.streak', { count: s.count ?? 0 }),
      subject: '',
      text: t('postComposer.starter.streakText', { count: s.count ?? 0 }),
    }
  const key = s.kind === 'completed' ? 'finished' : 'hit'
  return {
    icon:
      s.kind === 'completed' ? (
        <CircleCheck className="size-3.5 shrink-0 text-primary-text" />
      ) : (
        <Flag className="size-3.5 shrink-0 text-primary-text" />
      ),
    label: t(`postComposer.starter.${key}`, { title: SUBJECT }),
    subject,
    text: t(`postComposer.starter.${key}Text`, { title: subject }),
  }
}

/** New post / New story, switched by the Post | Story tabs; text and goal carry across. */
export function ComposerDialog({
  open,
  onOpenChange,
  initialMode = 'post',
  initialText,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialMode?: ComposerMode
  initialText?: string
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [mode, setMode] = useState(initialMode)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [picked, setPicked] = useState<Picked | null>(null)
  const [sharing, setSharing] = useState(false)
  const change = (o: boolean) => (o || !sharing) && onOpenChange(o)
  const close = () => change(false)
  useEffect(() => {
    if (open) setMode(initialMode)
    else setPicked(null)
  }, [open, initialMode])
  useEffect(() => {
    if (open && initialText) setDraft({ ...EMPTY, text: initialText, kind: 'win' })
  }, [open, initialText])
  useEffect(() => {
    if (!picked) return
    const url = picked.preview
    return () => URL.revokeObjectURL(url)
  }, [picked])
  const done = () => {
    setDraft(EMPTY)
    setPicked(null)
    onOpenChange(false)
  }
  const title = t(mode === 'post' ? 'postComposer.title' : 'stories.composeTitle')

  const body = (
    <div className="flex flex-col gap-3">
      {mode === 'post' ? (
        <PostComposer
          mobile={isMobile}
          draft={draft}
          setDraft={setDraft}
          onClose={close}
          onPosted={done}
        />
      ) : (
        <StoryComposer
          mobile={isMobile}
          draft={draft}
          setDraft={setDraft}
          picked={picked}
          setPicked={setPicked}
          busy={sharing}
          setBusy={setSharing}
          onClose={close}
          onPosted={done}
        />
      )}
      <ModeSwitch mode={mode} onChange={setMode} disabled={sharing} />
    </div>
  )

  if (isMobile)
    return (
      <Sheet open={open} onOpenChange={change}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="max-h-[92dvh] overflow-y-auto rounded-t-[22px] border-border bg-background px-4 pt-2 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] text-sm"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="mx-auto mb-2 h-1 w-9 shrink-0 rounded-full bg-muted-foreground/40" />
          {body}
        </SheetContent>
      </Sheet>
    )

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent showCloseButton={false} className="max-w-[470px] p-[18px]">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  )
}

function ModeSwitch({
  mode,
  onChange,
  disabled,
}: {
  mode: ComposerMode
  onChange: (m: ComposerMode) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <div role="tablist" className="mx-auto flex rounded-xl bg-muted p-1 text-[13px] font-bold">
      {(['post', 'story'] as const).map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          aria-selected={mode === m}
          disabled={disabled}
          onClick={() => onChange(m)}
          className={cn(
            'min-w-20 cursor-pointer rounded-lg px-4 py-1.5 transition-colors disabled:cursor-not-allowed',
            mode === m
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t(m === 'post' ? 'postComposer.modePost' : 'postComposer.modeStory')}
        </button>
      ))}
    </div>
  )
}

function SubmitButton({
  mobile,
  ready,
  busy,
  label,
  hint,
  onClick,
}: {
  mobile: boolean
  ready: boolean
  busy: boolean
  label: string
  hint?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!ready || busy}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border px-4 text-[13px] transition-colors disabled:cursor-not-allowed',
        mobile ? 'h-[30px] px-3.5' : 'h-[34px]',
        ready
          ? 'border-primary bg-primary font-extrabold text-primary-foreground hover:bg-primary/90'
          : 'border-border bg-muted/60 font-semibold text-muted-foreground/60',
      )}
    >
      {busy && (
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {label}
      {!mobile && ready && hint && (
        <span className="rounded-[5px] bg-black/15 px-1.5 py-px font-mono text-[10px] font-semibold">
          {hint}
        </span>
      )}
    </button>
  )
}

function CloseButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('common.close', { defaultValue: 'Close' })}
      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
    >
      <X className="h-4 w-4" />
    </button>
  )
}

function GoalCard({ goal, onRemove }: { goal: Goal; onRemove: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-2.5 py-2">
      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-[#5cb8ff]/14 text-[#1d74c4] dark:text-[#5cb8ff]">
        <Target className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <p className="truncate text-[12.5px] font-bold text-foreground">{goal.title}</p>
        <p className="text-[10.5px] text-muted-foreground">
          {t(`goals.cat.${goal.category.toLowerCase()}`, { defaultValue: goal.category })}
          {goal.milestoneCount > 0 &&
            ` · ${t('postComposer.milestones', { done: goal.completedMilestones, total: goal.milestoneCount })}`}
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('postComposer.removeGoal')}
        className="ml-auto cursor-pointer text-muted-foreground/70 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function GoalList({ goals, onPick }: { goals: Goal[]; onPick: (id: Id<'nodes'>) => void }) {
  const { t } = useTranslation()
  return (
    <div className="max-h-48 overflow-y-auto rounded-xl border border-border bg-card p-1">
      {goals.length === 0 ? (
        <p className="px-2.5 py-2 text-xs text-muted-foreground">{t('postComposer.noGoals')}</p>
      ) : (
        goals.map((g) => (
          <button
            key={g._id}
            type="button"
            onClick={() => onPick(g._id)}
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs hover:bg-muted"
          >
            <Target className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-foreground">{g.title}</span>
            <span className="ml-auto shrink-0 text-[10.5px] text-muted-foreground">
              {t(`goals.cat.${g.category.toLowerCase()}`, { defaultValue: g.category })}
            </span>
          </button>
        ))
      )}
    </div>
  )
}

function LinkGoalButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <Target className="h-3.5 w-3.5" />
      {t('postComposer.linkGoal')}
    </button>
  )
}

function PostComposer({
  mobile,
  draft,
  setDraft,
  onClose,
  onPosted,
}: {
  mobile: boolean
  draft: Draft
  setDraft: SetDraft
  onClose: () => void
  onPosted: () => void
}) {
  const { t } = useTranslation()
  const handle = useQuery(api.preferences.getMyHandle)
  const me = useQuery(api.auth.getCurrentUser)
  const starters = useQuery(api.posts.postStarters) ?? []
  const goals = (useGoals() ?? []).filter((g) => !g.isInbox)
  const createPost = useMutation(api.posts.createPost)
  const [saving, setSaving] = useState(false)
  const [picking, setPicking] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`
  }, [draft.text, handle])

  if (handle === undefined) return <div className="h-40" />
  if (handle === null) return <HandleStep onCancel={onClose} />

  const name = (me?.name as string | undefined) ?? ''
  const text = draft.text
  const hasText = text.trim().length > 0
  const len = text.length
  const goal = goals.find((g) => g._id === draft.goalId)
  const visibleStarters = hasText
    ? starters.filter((s) => starterKey(s) === draft.starter)
    : starters

  function pickStarter(s: Starter) {
    const key = starterKey(s)
    if (draft.starter === key) {
      setDraft((d) => ({ ...d, starter: null }))
      return
    }
    const copy = starterCopy(t, s)
    setDraft((d) => ({
      text: `${copy.text}\n`,
      kind: s.postKind,
      goalId: s.goalId ?? d.goalId,
      starter: key,
    }))
    requestAnimationFrame(() => {
      const el = taRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    })
  }

  async function submit() {
    const trimmed = text.trim()
    if (!trimmed || saving || !handle) return
    setSaving(true)
    try {
      const id = await createPost({
        content: trimmed,
        ...(draft.kind ? { kind: draft.kind } : {}),
        ...(draft.goalId ? { goalId: draft.goalId } : {}),
      })
      onPosted()
      const url = postPermalink(handle, id)
      toast.custom(
        (tid) => <PostedToast text={trimmed} url={url} onDismiss={() => toast.dismiss(tid)} />,
        {
          duration: 10_000,
        },
      )
    } catch {
      toast.error(t('postComposer.failed'))
    } finally {
      setSaving(false)
    }
  }

  const postButton = (
    <SubmitButton
      mobile={mobile}
      ready={hasText}
      busy={saving}
      label={t('postComposer.post')}
      hint={isMacPlatform() ? '⌘⏎' : 'Ctrl⏎'}
      onClick={() => void submit()}
    />
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <CanopyAvatar userId={me?._id as string | undefined}>
          <UserAvatar
            image={me?.image as string | undefined}
            email={me?.email as string | undefined}
            name={name}
            size={68}
            className={cn(
              'shrink-0 rounded-full object-cover',
              mobile ? 'h-7 w-7' : 'h-[34px] w-[34px]',
            )}
          />
        </CanopyAvatar>
        <div className="min-w-0">
          <p className="truncate text-[13.5px] font-bold text-foreground">
            {mobile || !name ? `@${handle}` : `${name} · @${handle}`}
          </p>
          <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Globe className="h-3 w-3 shrink-0" />
            {t('postComposer.publicOnProfile')}
            {!mobile && (
              <a
                href={profileUrl(handle)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary-text hover:underline"
              >
                {t('postComposer.view')} ↗
              </a>
            )}
          </p>
        </div>
        <div className="ml-auto">{mobile ? postButton : <CloseButton onClick={onClose} />}</div>
      </div>

      {visibleStarters.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {!hasText && (
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70">
              {t('postComposer.startFromToday')}
            </span>
          )}
          <div
            className={cn(
              'flex gap-1.5',
              mobile ? '-mx-4 overflow-x-auto px-4 [scrollbar-width:none]' : 'flex-wrap',
            )}
          >
            {visibleStarters.map((s) => {
              const key = starterKey(s)
              const copy = starterCopy(t, s)
              const [before, after] = copy.label.split(SUBJECT)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickStarter(s)}
                  className={cn(
                    'inline-flex max-w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-[10px] border px-2.5 py-[5px] text-xs text-foreground transition-colors',
                    draft.starter === key
                      ? 'border-primary/45 bg-primary/13'
                      : 'border-border bg-card hover:bg-muted',
                  )}
                >
                  {copy.icon}
                  <span className={cn('truncate', mobile ? 'max-w-[180px]' : 'max-w-[300px]')}>
                    {after === undefined ? (
                      before
                    ) : (
                      <>
                        {before}
                        <em className="not-italic text-muted-foreground">{copy.subject}</em>
                        {after}
                      </>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <textarea
        ref={taRef}
        value={text}
        rows={2}
        autoFocus
        maxLength={POST_MAX}
        onChange={(e) => {
          const value = e.target.value.slice(0, POST_MAX)
          setDraft((d) => ({ ...d, text: value, ...(value.trim() ? {} : { starter: null }) }))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void submit()
          }
        }}
        placeholder={t('postComposer.placeholder')}
        className={cn(
          'w-full resize-none rounded-xl border-[1.5px] border-primary/45 bg-card px-3.5 py-3 text-[13.5px] leading-[1.45] text-foreground outline-none transition-shadow placeholder:text-muted-foreground/60',
          hasText && 'ring-4 ring-primary/13',
          mobile && 'min-h-[60px]',
        )}
      />

      <div className="flex flex-wrap gap-1.5">
        {POST_KINDS.map((k) => {
          const selected = draft.kind === k
          return (
            <button
              key={k}
              type="button"
              aria-pressed={selected}
              onClick={() => setDraft((d) => ({ ...d, kind: selected ? null : k }))}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11.5px] transition-colors',
                selected
                  ? cn('border-current', KIND_TEXT[k])
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              <i
                className="block h-1.5 w-1.5 rounded-full"
                style={{ background: POST_KIND_COLORS[k] }}
              />
              {t(`postComposer.kind.${k}`)}
            </button>
          )
        })}
      </div>

      {goal && <GoalCard goal={goal} onRemove={() => setDraft((d) => ({ ...d, goalId: null }))} />}

      {picking && !goal && (
        <GoalList
          goals={goals}
          onPick={(goalId) => {
            setDraft((d) => ({ ...d, goalId }))
            setPicking(false)
          }}
        />
      )}

      <div className="flex items-center gap-2 border-t border-border pt-3">
        {!goal && <LinkGoalButton onClick={() => setPicking((p) => !p)} />}
        {len >= 400 && (
          <span
            className={cn(
              'font-mono text-[11.5px]',
              len >= 480 ? 'text-amber-500 dark:text-amber-400' : 'text-muted-foreground',
            )}
          >
            {len}/{POST_MAX}
          </span>
        )}
        <span className="flex-1" />
        {!mobile && postButton}
      </div>
    </div>
  )
}

function StoryComposer({
  mobile,
  draft,
  setDraft,
  picked,
  setPicked,
  busy,
  setBusy,
  onClose,
  onPosted,
}: {
  mobile: boolean
  draft: Draft
  setDraft: SetDraft
  picked: Picked | null
  setPicked: (p: Picked) => void
  busy: boolean
  setBusy: (b: boolean) => void
  onClose: () => void
  onPosted: () => void
}) {
  const { t } = useTranslation()
  const me = useQuery(api.auth.getCurrentUser)
  const goals = (useGoals() ?? []).filter((g) => !g.isInbox)
  const uploadUrl = useMutation(api.habitPhotos.uploadUrl)
  const post = useMutation(api.stories.post)
  const [picking, setPicking] = useState(false)
  const [edit, setEdit, resetEdit] = usePhotoEdit()
  const [selected, setSelected] = useState<StickerKind | null>(null)
  const specs = useStoryStickers()
  const input = useRef<HTMLInputElement>(null)
  const caption = toCaption(draft.text)
  const goal = goals.find((g) => g._id === draft.goalId)
  const pick = () => input.current?.click()
  const fail = (key: string) =>
    toast.error(
      t(key, { seconds: STORY_VIDEO_MAX_SECONDS, mb: Math.round(STORY_VIDEO_MAX_BYTES / MB) }),
    )

  const onFile = async (file: File | undefined) => {
    if (!file) return
    if (file.type.startsWith('video/')) {
      if (file.size > STORY_VIDEO_MAX_BYTES) return fail('stories.tooLarge')
      const clip = await readStoryClip(file).catch(() => null)
      if (!clip) return fail('stories.badFile')
      // Camera clips stopped at the limit can run a few frames over.
      if (Number.isFinite(clip.duration) && clip.duration > STORY_VIDEO_MAX_SECONDS + 0.25)
        return fail('stories.tooLong')
      setPicked({ kind: 'video', file, ...clip, preview: URL.createObjectURL(file) })
    } else if (file.type.startsWith('image/')) {
      const blob = await downscaleImage(file).catch(() => null)
      if (!blob) return fail('stories.badFile')
      if (blob.size > STORY_PHOTO_MAX_BYTES) return fail('stories.tooLarge')
      resetEdit()
      setSelected(null)
      setPicked({ kind: 'photo', file, blob, preview: URL.createObjectURL(blob) })
    } else fail('stories.badFile')
  }

  const upload = async (blob: Blob, type: string) => {
    const res = await fetch(await uploadUrl({}), {
      method: 'POST',
      headers: { 'Content-Type': type },
      body: blob,
    })
    if (!res.ok) throw new Error('upload')
    return ((await res.json()) as { storageId: Id<'_storage'> }).storageId
  }

  const share = async () => {
    if (!picked || busy) return
    setBusy(true)
    try {
      const common = { caption: caption.trim() || undefined, goalId: draft.goalId ?? undefined }
      const photo =
        picked.kind === 'photo' && isEdited(edit)
          ? await bakePhoto(picked.blob, edit, specs, {
              max: STORY_PHOTO_MAX_PX,
              quality: STORY_PHOTO_QUALITY,
              aspect: STORY_ASPECT,
            })
          : picked.kind === 'photo'
            ? picked.blob
            : null
      if (photo && photo.size > STORY_PHOTO_MAX_BYTES) {
        fail('stories.tooLarge')
        return
      }
      const out =
        picked.kind === 'photo'
          ? await post({
              kind: 'photo',
              storageId: await upload(photo ?? picked.blob, 'image/jpeg'),
              ...common,
            })
          : await post({
              kind: 'video',
              storageId: await upload(
                picked.file,
                picked.file.type.startsWith('video/') ? picked.file.type : 'video/mp4',
              ),
              posterStorageId: await upload(picked.poster, 'image/jpeg'),
              frameStorageIds: await Promise.all(picked.frames.map((f) => upload(f, 'image/jpeg'))),
              ...common,
            })
      if (!out.ok) {
        toast.error(
          t(
            out.code === 'tooLarge'
              ? 'stories.tooLarge'
              : out.code === 'tooMany'
                ? 'stories.tooMany'
                : 'stories.postFailed',
            { mb: Math.round(STORY_VIDEO_MAX_BYTES / MB) },
          ),
        )
        return
      }
      toast.success(t('stories.posted'))
      onPosted()
    } catch {
      toast.error(t('stories.postFailed'))
    } finally {
      setBusy(false)
    }
  }

  const shareButton = (
    <SubmitButton
      mobile={mobile}
      ready={!!picked}
      busy={busy}
      label={t(busy ? 'stories.sharing' : 'stories.share')}
      onClick={() => void share()}
    />
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <CanopyAvatar userId={me?._id as string | undefined}>
          <UserAvatar
            image={me?.image as string | undefined}
            email={me?.email as string | undefined}
            name={(me?.name as string | undefined) ?? ''}
            size={68}
            className={cn(
              'shrink-0 rounded-full object-cover',
              mobile ? 'h-7 w-7' : 'h-[34px] w-[34px]',
            )}
          />
        </CanopyAvatar>
        <p className="min-w-0 truncate text-[13.5px] font-bold text-foreground">
          {t('stories.yourStory')}
        </p>
        <div className="ml-auto">
          {mobile ? shareButton : <CloseButton onClick={() => !busy && onClose()} />}
        </div>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*,video/*"
        hidden
        onChange={(e) => {
          void onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <div className="relative mx-auto aspect-[9/16] h-[min(52dvh,400px)] overflow-hidden rounded-xl bg-black">
        {!picked ? (
          <button
            type="button"
            onClick={pick}
            className="flex size-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-border bg-card px-4 text-center transition-colors hover:bg-muted"
          >
            <span className="grid size-12 place-items-center rounded-full bg-primary/13 text-primary-text">
              <ImagePlus className="size-6" />
            </span>
            <span className="text-[13.5px] font-bold text-foreground">
              {t('stories.pickTitle')}
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              {t('stories.pickHint', { seconds: STORY_VIDEO_MAX_SECONDS })}
            </span>
          </button>
        ) : (
          <>
            {picked.kind === 'video' ? (
              <video
                src={picked.preview}
                autoPlay
                loop
                muted
                playsInline
                className="size-full object-cover"
              />
            ) : (
              <PhotoEditorCanvas
                source={picked.blob}
                aspect={STORY_ASPECT}
                specs={specs}
                edit={edit}
                onEdit={setEdit}
                selected={selected}
                onSelect={setSelected}
                className="size-full"
              />
            )}
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md hover:bg-black/70 disabled:opacity-50"
            >
              {t('stories.change')}
            </button>
          </>
        )}
      </div>

      {picked?.kind === 'photo' && (
        <PhotoEditorControls
          source={picked.blob}
          specs={specs}
          edit={edit}
          onEdit={setEdit}
          selected={selected}
          onSelect={setSelected}
          disabled={busy}
        />
      )}

      <input
        value={caption}
        onChange={(e) => {
          const value = e.target.value.slice(0, STORY_CAPTION_MAX)
          setDraft((d) => ({ ...d, text: value, ...(value.trim() ? {} : { starter: null }) }))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void share()
          }
        }}
        maxLength={STORY_CAPTION_MAX}
        placeholder={t('stories.captionPlaceholder')}
        aria-label={t('stories.captionPlaceholder')}
        className="w-full rounded-xl border-[1.5px] border-primary/45 bg-card px-3.5 py-2.5 text-[13.5px] text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-4 focus:ring-primary/13"
      />

      {goal && <GoalCard goal={goal} onRemove={() => setDraft((d) => ({ ...d, goalId: null }))} />}

      {picking && !goal && (
        <GoalList
          goals={goals}
          onPick={(goalId) => {
            setDraft((d) => ({ ...d, goalId }))
            setPicking(false)
          }}
        />
      )}

      <div className="flex items-center gap-2 border-t border-border pt-3">
        {!goal && <LinkGoalButton onClick={() => setPicking((p) => !p)} />}
        <span className="flex-1" />
        {!mobile && shareButton}
      </div>
    </div>
  )
}

function HandleStep({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation()
  const suggestion = useQuery(api.preferences.suggestHandle)
  const setHandle = useMutation(api.preferences.setHandle).withOptimisticUpdate(optimisticSetHandle)
  const [value, setValue] = useState('')
  const [debounced, setDebounced] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (suggestion && !value) setValue(suggestion)
  }, [suggestion])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), 250)
    return () => clearTimeout(id)
  }, [value])

  const check = useQuery(api.preferences.checkHandle, debounced ? { handle: debounced } : 'skip')
  const settled = debounced === value && check !== undefined
  const ok = settled && check.valid && check.available

  async function save() {
    if (!ok || saving) return
    setSaving(true)
    try {
      await setHandle({ handle: value })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('postComposer.failed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-[13.5px] font-bold text-foreground">{t('postComposer.handle.title')}</p>
        <p className="text-[11.5px] text-muted-foreground">{t('postComposer.handle.subtitle')}</p>
      </div>
      <label className="flex items-center rounded-xl border border-border bg-card px-3 py-2.5 font-mono text-[13px] text-foreground focus-within:border-primary/45">
        <span className="text-muted-foreground/60">@</span>
        <input
          value={value}
          autoFocus
          maxLength={30}
          onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save()
          }}
          className="min-w-0 flex-1 bg-transparent outline-none"
        />
        {settled && value && (
          <span
            className={cn(
              'ml-2 shrink-0 font-sans text-[11.5px] font-semibold',
              ok ? 'text-[#3f8f1f] dark:text-[#7ed957]' : 'text-destructive',
            )}
          >
            {ok ? (
              <span className="inline-flex items-center gap-1">
                <Check className="h-3 w-3" />
                {t('postComposer.handle.available')}
              </span>
            ) : check.valid ? (
              t('postComposer.handle.taken')
            ) : (
              t('postComposer.handle.invalid')
            )}
          </span>
        )}
      </label>
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <span className="flex-1" />
        <button
          type="button"
          onClick={onCancel}
          className="h-[34px] cursor-pointer rounded-full border border-border px-4 text-[13px] font-semibold text-foreground hover:bg-muted"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!ok || saving}
          className="h-[34px] cursor-pointer rounded-full bg-primary px-4 text-[13px] font-extrabold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('postComposer.handle.continue')}
        </button>
      </div>
    </div>
  )
}
